if (!customElements.get('index-video')) {
  class IndexVideo extends HTMLElement {
    connectedCallback() {
      // Below-the-fold: wait until near viewport before Swiper / video work.
      this.isInView = false;
      this.hasBooted = false;
      this.scaleRaf = 0;
      this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if ('IntersectionObserver' in window) {
        this.viewObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              this.isInView = entry.isIntersecting;
              if (entry.isIntersecting) {
                this.boot();
                this.onEnterView();
              } else {
                this.onLeaveView();
              }
            });
          },
          { rootMargin: '280px 0px', threshold: 0.01 }
        );
        this.viewObserver.observe(this);
      } else {
        this.boot();
        this.isInView = true;
      }
    }

    disconnectedCallback() {
      this.viewObserver?.disconnect();
      this.pauseAllVideos();
      clearTimeout(this.animTimer);
      if (this.scaleRaf) cancelAnimationFrame(this.scaleRaf);
      this.swiper?.destroy(true, true);
      this.swiper = null;
    }

    boot() {
      if (this.hasBooted) return;
      this.hasBooted = true;
      this.setup();
    }

    setup() {
      this.swiperEl = this.querySelector('.index-video__swiper');
      this.wrapper = this.querySelector('.swiper-wrapper');
      this.prevButton = this.querySelector('.index-video__nav--prev');
      this.nextButton = this.querySelector('.index-video__nav--next');
      this.defaultIndex = Number.parseInt(this.dataset.defaultActive, 10) || 0;
      this.autoplaySpeed = Number.parseInt(this.dataset.autoplaySpeed, 10) || 5;
      this.autoplayDelay = this.autoplaySpeed * 1000;
      this.originalCount = Number.parseInt(this.dataset.slideCount, 10) || 0;
      this.modal = document.getElementById(this.dataset.modalId);
      this.modalMedia = this.modal?.querySelector('.index-video__modal-media');
      this.modalAvatar = this.modal?.querySelector('.index-video__modal-avatar');
      this.modalName = this.modal?.querySelector('.index-video__modal-name');
      this.modalDescription = this.modal?.querySelector('.index-video__modal-description');
      this.modalComment = this.modal?.querySelector('.index-video__modal-comment');
      this.modalMore = this.modal?.querySelector('.index-video__modal-more');
      this.advanceLocked = false;
      this.isAnimating = false;
      this.animTimer = null;
      this.swiper = null;
      this.eventsBound = false;

      if (!this.swiperEl || !this.wrapper || !this.originalCount) return;

      this.prepareLoopSlides();
      this.silenceVideos();

      this.waitForSwiper().then(() => {
        if (!this.isConnected) return;
        this.initSwiper();
        this.bindEvents();
        if (this.isInView) this.onEnterView();
      });
    }

    silenceVideos() {
      this.querySelectorAll('.index-video__preview').forEach((video) => {
        video.preload = 'none';
        video.removeAttribute('autoplay');
      });
    }

    setAnimating(active) {
      this.isAnimating = active;
      clearTimeout(this.animTimer);
      if (active) {
        this.animTimer = setTimeout(() => {
          this.isAnimating = false;
        }, 900);
      }
    }

    waitForSwiper() {
      if (typeof window.Swiper !== 'undefined') return Promise.resolve();

      return new Promise((resolve) => {
        let frames = 0;
        const tick = () => {
          if (typeof window.Swiper !== 'undefined' || frames > 180) {
            resolve();
            return;
          }
          frames += 1;
          requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      });
    }

    prepareLoopSlides() {
      const originals = Array.from(
        this.wrapper.querySelectorAll('.index-video__slide:not([data-loop-fill])')
      );
      if (originals.length < 2) return;

      const minSlides = Math.max(12, originals.length * 3);
      let guard = 0;

      while (this.wrapper.querySelectorAll('.index-video__slide').length < minSlides && guard < 40) {
        const source = originals[guard % originals.length];
        const clone = source.cloneNode(true);
        clone.setAttribute('data-loop-fill', 'true');
        clone.removeAttribute('id');
        clone.querySelectorAll('.index-video__preview').forEach((video) => {
          video.preload = 'none';
          video.pause();
        });
        this.wrapper.appendChild(clone);
        guard += 1;
      }

      this.slideCount = this.wrapper.querySelectorAll('.index-video__slide').length;
    }

    initSwiper() {
      if (typeof window.Swiper === 'undefined' || this.swiper) return;

      const total = this.slideCount || this.originalCount;
      const initialSlide = Math.min(Math.max(this.defaultIndex, 0), Math.max(total - 1, 0));
      const enableLoop = total > 3;
      const enableAutoplay = !this.prefersReducedMotion;

      this.swiper = new window.Swiper(this.swiperEl, {
        effect: 'slide',
        grabCursor: true,
        centeredSlides: true,
        slidesPerView: 'auto',
        spaceBetween: 18,
        loop: enableLoop,
        loopedSlides: enableLoop ? Math.max(this.originalCount, 4) : undefined,
        loopAdditionalSlides: enableLoop ? Math.max(6, this.originalCount) : 0,
        speed: 650,
        initialSlide,
        watchSlidesProgress: true,
        resistanceRatio: 0.65,
        longSwipesRatio: 0.25,
        followFinger: true,
        allowTouchMove: true,
        autoplay: enableAutoplay
          ? {
              delay: this.autoplayDelay,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
            }
          : false,
        on: {
          init: (swiper) => {
            this.updateSlideScales(swiper);
            if (this.isInView) this.syncActiveVideo();
          },
          setTranslate: (swiper) => {
            this.scheduleScaleUpdate(swiper);
          },
          setTransition: (swiper, duration) => {
            swiper.slides.forEach((slide) => {
              const card = slide.querySelector('.index-video__card');
              if (card) card.style.transitionDuration = `${duration}ms`;
            });
          },
          slideChangeTransitionStart: () => {
            this.setAnimating(true);
            this.pauseAllVideos();
          },
          slideChangeTransitionEnd: () => {
            this.setAnimating(false);
            if (this.advanceLocked || !this.isInView) return;
            this.syncActiveVideo();
          },
          transitionEnd: (swiper) => {
            this.setAnimating(false);
            this.scheduleScaleUpdate(swiper);
          },
        },
      });

      if (!this.isInView) this.pauseAutoplay();
    }

    scheduleScaleUpdate(swiper) {
      if (this.scaleRaf) cancelAnimationFrame(this.scaleRaf);
      this.scaleRaf = requestAnimationFrame(() => {
        this.scaleRaf = 0;
        this.updateSlideScales(swiper);
      });
    }

    updateSlideScales(swiper) {
      if (!swiper?.slides) return;

      swiper.slides.forEach((slide) => {
        const progress = Math.min(Math.abs(slide.progress || 0), 2);
        const scale = Math.max(0.78, 1 - progress * 0.14);
        const opacity = Math.max(0.88, 1 - progress * 0.06);
        const card = slide.querySelector('.index-video__card');

        if (card) {
          card.style.transform = `scale(${scale})`;
          card.style.opacity = String(opacity);
        }

        slide.style.zIndex = String(Math.round(20 - progress * 5));
      });
    }

    onEnterView() {
      if (!this.swiper || this.advanceLocked) return;
      this.resumeAutoplay();
      this.syncActiveVideo();
    }

    onLeaveView() {
      this.pauseAutoplay();
      this.pauseAllVideos();
    }

    goToSlideEl(slideEl) {
      if (!this.swiper || !slideEl) return;

      const slides = this.swiper.slides;
      let targetIndex = -1;

      for (let i = 0; i < slides.length; i += 1) {
        if (slides[i] === slideEl) {
          targetIndex = i;
          break;
        }
      }

      if (targetIndex < 0) return;
      if (targetIndex === this.swiper.activeIndex) return;

      this.swiper.slideTo(targetIndex, 650);
    }

    navigate(direction) {
      if (!this.swiper) return;

      this.setAnimating(false);

      if (direction === 'prev') {
        this.swiper.slidePrev();
      } else {
        this.swiper.slideNext();
      }
    }

    pauseAutoplay() {
      this.swiper?.autoplay?.stop();
    }

    resumeAutoplay() {
      if (this.advanceLocked || this.prefersReducedMotion || !this.isInView || !this.swiper?.autoplay) {
        return;
      }
      this.swiper.params.autoplay.delay = this.autoplayDelay;
      this.swiper.autoplay.start();
    }

    bindEvents() {
      if (this.eventsBound) return;
      this.eventsBound = true;

      this.addEventListener('click', (event) => {
        if (event.target.closest('.index-video__nav')) return;
        if (event.target.closest('.index-video__more')) return;

        const media = event.target.closest('.index-video__media--clickable');
        if (media) {
          event.preventDefault();
          event.stopPropagation();
          const slide = media.closest('.index-video__slide');
          if (slide) this.openModal(slide);
          return;
        }

        const slide = event.target.closest('.index-video__slide');
        if (!slide) return;

        this.goToSlideEl(slide);
      });

      this.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;

        const media = event.target.closest('.index-video__media--clickable');
        if (!media) return;

        event.preventDefault();
        const slide = media.closest('.index-video__slide');
        if (slide) this.openModal(slide);
      });

      this.prevButton?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.navigate('prev');
      });

      this.nextButton?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.navigate('next');
      });

      this.modal?.addEventListener('click', (event) => {
        if (event.target === this.modal) {
          this.modal.hide();
          this.unlockAdvance();
        }
      });

      this.modal?.querySelector('[id^="ModalClose-"]')?.addEventListener('click', () => {
        this.unlockAdvance();
      });
    }

    getActiveSlide() {
      return this.swiper?.slides[this.swiper.activeIndex] || null;
    }

    pauseAllVideos() {
      this.querySelectorAll('.index-video__preview').forEach((video) => {
        video.pause();
        try {
          video.currentTime = 0;
        } catch (error) {
          // ignore seek errors on unloaded media
        }
        video.closest('.index-video__media')?.classList.remove('index-video__media--playing');
      });
    }

    syncActiveVideo() {
      if (this.advanceLocked || !this.isInView) return;

      this.pauseAllVideos();

      const activeSlide = this.getActiveSlide();
      const media = activeSlide?.querySelector('.index-video__media');
      const video = activeSlide?.querySelector('.index-video__preview');
      if (!video) return;

      video.loop = false;
      video.muted = true;
      video.playsInline = true;
      video.preload = 'metadata';

      try {
        video.currentTime = 0;
      } catch (error) {
        // ignore
      }

      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            media?.classList.add('index-video__media--playing');
          })
          .catch(() => {
            media?.classList.remove('index-video__media--playing');
          });
      }
    }

    lockAdvance() {
      this.advanceLocked = true;
      this.pauseAutoplay();
      this.pauseAllVideos();
    }

    unlockAdvance() {
      this.advanceLocked = false;
      if (this.isInView) {
        this.syncActiveVideo();
        this.resumeAutoplay();
      }
    }

    getSourceSlide(slide) {
      const index = slide.getAttribute('data-swiper-slide-index') || slide.dataset.index;
      if (index == null) return slide;

      const original = this.querySelector(
        `.index-video__slide[data-index="${index}"]:not(.swiper-slide-duplicate):not([data-loop-fill])`
      );
      return (
        original ||
        this.querySelector(`.index-video__slide[data-index="${index}"]:not(.swiper-slide-duplicate)`) ||
        slide
      );
    }

    fillModalInfo(sourceSlide) {
      const author = sourceSlide.dataset.author || '';
      const description = sourceSlide.dataset.description || '';
      const comment = sourceSlide.dataset.comment || '';
      const linkUrl = sourceSlide.dataset.linkUrl || '';
      const linkText = sourceSlide.dataset.linkText || 'View More';
      const avatarUrl = sourceSlide.dataset.avatar || '';

      if (this.modalAvatar) {
        this.modalAvatar.replaceChildren();
        if (avatarUrl) {
          const img = document.createElement('img');
          img.src = avatarUrl;
          img.alt = author;
          img.width = 64;
          img.height = 64;
          img.loading = 'lazy';
          img.decoding = 'async';
          this.modalAvatar.appendChild(img);
          this.modalAvatar.hidden = false;
          this.modalAvatar.classList.add('is-visible');
        } else {
          this.modalAvatar.hidden = true;
          this.modalAvatar.classList.remove('is-visible');
        }
      }

      if (this.modalName) {
        this.modalName.textContent = author;
        this.modalName.hidden = !author;
      }

      if (this.modalDescription) {
        this.modalDescription.textContent = description;
        this.modalDescription.hidden = !description;
      }

      if (this.modalComment) {
        this.modalComment.textContent = comment;
        this.modalComment.hidden = !comment;
      }

      if (this.modalMore) {
        if (linkUrl) {
          this.modalMore.href = linkUrl;
          this.modalMore.textContent = linkText || 'View More';
          this.modalMore.hidden = false;
          this.modalMore.classList.add('is-visible');
        } else {
          this.modalMore.removeAttribute('href');
          this.modalMore.hidden = true;
          this.modalMore.classList.remove('is-visible');
        }
      }
    }

    openModal(slide) {
      if (!this.modal || !this.modalMedia) return;

      this.lockAdvance();

      const sourceSlide = this.getSourceSlide(slide);
      const template = sourceSlide.querySelector('.index-video__detail-template');
      const preview = sourceSlide.querySelector('.index-video__preview');

      this.fillModalInfo(sourceSlide);
      this.modalMedia.replaceChildren();

      if (template) {
        this.modalMedia.appendChild(template.content.cloneNode(true));
      } else if (preview) {
        const video = document.createElement('video');
        video.className = 'index-video__detail';
        video.controls = true;
        video.playsInline = true;
        video.autoplay = true;

        preview.querySelectorAll('source').forEach((source) => {
          const nextSource = document.createElement('source');
          nextSource.src = source.src;
          nextSource.type = source.type;
          video.appendChild(nextSource);
        });

        if (!video.querySelector('source') && (preview.currentSrc || preview.src)) {
          video.src = preview.currentSrc || preview.src;
        }

        this.modalMedia.appendChild(video);
      } else {
        return;
      }

      const video = this.modalMedia.querySelector('video');
      if (video) {
        video.muted = false;
        video.controls = true;
        video.preload = 'metadata';
        video.load();
        video.play().catch(() => {});
      }

      const trigger = slide.querySelector('.index-video__media') || slide;
      this.modal.show(trigger);
    }
  }

  customElements.define('index-video', IndexVideo);
}
